import type { AddressFormat } from './types/address'

import {
  BKK,
  DISTRICT_PREFIX,
  PROVINCE_PREFIX,
  SUBDISTRICT_PREFIX,
} from './constants/address'

export interface GetAddressOption {
  separator?: string
  hasCountry?: boolean
  hasPrefix?: boolean
  hasProvincePrefix?: boolean
  isShortPrefix?: boolean
}

const defaultOption = {
  separator: ' ',
  hasCountry: true,
  hasPrefix: true,
}

const getProvincePrefix = (
  isLangEn: boolean,
  isShortPrefix?: boolean,
): string => {
  if (isLangEn) {
    return PROVINCE_PREFIX.EN
  }

  return isShortPrefix ? PROVINCE_PREFIX.TH.SHORT : PROVINCE_PREFIX.TH.CMM
}

const getDistrictPrefix = (
  isLangEn: boolean,
  isShortPrefix?: boolean,
  isBkk?: boolean,
): string => {
  if (isLangEn) {
    return DISTRICT_PREFIX.EN
  }

  if (isBkk) {
    return DISTRICT_PREFIX.TH.BKK // เขต
  }

  return isShortPrefix
    ? DISTRICT_PREFIX.TH.SHORT // อ.
    : DISTRICT_PREFIX.TH.CMM // อำเภอ
}

const getSubdistrictPrefix = (
  isLangEn: boolean,
  isShortPrefix?: boolean,
  isBkk?: boolean,
): string => {
  if (isLangEn) {
    return SUBDISTRICT_PREFIX.EN
  }

  if (isBkk) {
    return SUBDISTRICT_PREFIX.TH.BKK // เขต
  }

  return isShortPrefix
    ? SUBDISTRICT_PREFIX.TH.SHORT // ต.
    : SUBDISTRICT_PREFIX.TH.CMM // ตำบล
}

const getPrefix = (
  hasPrefix: boolean,
  province: string,
  isLangEn: boolean,
  isShortPrefix?: boolean,
  hasProvincePrefix?: boolean,
): string[] => {
  let provincePrefix = ''
  let districtPrefix = ''
  let subdistrictPrefix = ''

  if (hasPrefix) {
    const bkks = Object.keys(BKK).filter((key) => isNaN(Number(key)))
    const isBkk = bkks.includes(province)

    if (hasProvincePrefix) {
      provincePrefix = getProvincePrefix(isLangEn, isShortPrefix)
    }

    districtPrefix = getDistrictPrefix(isLangEn, isShortPrefix, isBkk)
    subdistrictPrefix = getSubdistrictPrefix(isLangEn, isShortPrefix, isBkk)
  }

  return [provincePrefix, districtPrefix, subdistrictPrefix]
}

export const getFullAddress = (
  language: 'en' | 'th',
  address: AddressFormat,
  option: GetAddressOption = defaultOption,
): string => {
  const {
    separator = ' ',
    hasCountry = true,
    hasPrefix = true,
    hasProvincePrefix,
    isShortPrefix,
  } = option

  // Setup short language
  const lang = language.charAt(0).toUpperCase() + language.slice(1)
  const isLangEn = lang === 'En'

  // Setup address unit
  let country = address.countryTh
  let province = address.provinceTh
  let district = address.districtTh
  let subdistrict = address.subDistrictTh

  if (isLangEn) {
    country = address.countryEn
    province = address.provinceEn
    district = address.districtEn
    subdistrict = address.subDistrictEn
  }

  // Setup prefix for province,district and subdistrict
  const [provincePrefix, districtPrefix, subdistrictPrefix] = getPrefix(
    hasPrefix,
    province,
    isLangEn,
    isShortPrefix,
    hasProvincePrefix,
  )

  let mergeProvince = province

  if (hasProvincePrefix) {
    mergeProvince = isLangEn
      ? `${provincePrefix} ${province}`
      : provincePrefix + province
  }

  const mergeDistrict = isLangEn
    ? `${districtPrefix} ${district}`
    : districtPrefix + district
  const mergeSubdistrict = isLangEn
    ? `${subdistrictPrefix} ${subdistrict}`
    : subdistrictPrefix + subdistrict

  const fullAddress = [
    address.address,
    mergeSubdistrict,
    mergeDistrict,
    mergeProvince,
    hasCountry ? country : '',
    address.postalCode,
  ].filter((text) => Boolean(text))

  return fullAddress.join(separator)
}
